import { useState, useEffect, useRef } from 'react'
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react'
import {
  PageHeader, Card, CardHeader, CardBody, Button, Badge,
  LoadingSpinner, ErrorMessage, SuccessMessage
} from '../components/common'
import { knowledgeBaseApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'
import { useAuth } from '../contexts/AuthContext'

const statusConfig = {
  STARTING: { icon: Clock, color: 'text-amber-500', badge: 'warning', label: 'Starting' },
  IN_PROGRESS: { icon: RefreshCw, color: 'text-blue-500', badge: 'info', label: 'In Progress', spin: true },
  COMPLETE: { icon: CheckCircle, color: 'text-green-500', badge: 'success', label: 'Complete' },
  FAILED: { icon: XCircle, color: 'text-red-500', badge: 'danger', label: 'Failed' },
  STOPPING: { icon: AlertTriangle, color: 'text-amber-500', badge: 'warning', label: 'Stopping' },
  STOPPED: { icon: XCircle, color: 'text-slate-500', badge: 'default', label: 'Stopped' },
}

export default function KnowledgeSync() {
  const [syncStatus, setSyncStatus] = useState(null)
  const [triggering, setTriggering] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const pollRef = useRef(null)

  const { selectedBuildingId: buildingId } = useAuth()

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const triggerSync = async () => {
    try {
      setTriggering(true)
      setError(null)
      const response = await knowledgeBaseApi.triggerSync()
      setSyncStatus(response.data)
      setSuccess('Sync job started')
      setTimeout(() => setSuccess(null), 3000)

      // Start polling
      startPolling(response.data.ingestionJobId)
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setTriggering(false)
    }
  }

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const response = await knowledgeBaseApi.getSyncStatus(jobId)
        setSyncStatus(response.data)

        // Stop polling when done
        if (['COMPLETE', 'FAILED', 'STOPPED'].includes(response.data.status)) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      } catch {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }, 5000)
  }

  const config = syncStatus ? (statusConfig[syncStatus.status] || statusConfig.STARTING) : null
  const StatusIcon = config?.icon || Clock

  return (
    <div className="space-y-6">
      <PageHeader
        title="Knowledge Base Sync"
        subtitle="Sync uploaded documents with AWS Bedrock for AI-powered search"
        backLink={{ label: 'Back to Knowledge Base', href: '/knowledge-base' }}
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {success && <SuccessMessage message={success} />}

      {/* Trigger Sync */}
      <Card>
        <CardHeader title="Sync with Bedrock" />
        <CardBody>
          <p className="text-sm text-slate-600 mb-4">
            Trigger an ingestion job to sync all uploaded documents with the Bedrock Knowledge Base.
            This makes documents searchable and available for the AI chat feature.
          </p>
          <Button variant="primary" icon={<RefreshCw size={16} />} onClick={triggerSync}
            loading={triggering} disabled={triggering || pollRef.current}>
            {triggering ? 'Starting...' : pollRef.current ? 'Sync in Progress...' : 'Start Sync'}
          </Button>
        </CardBody>
      </Card>

      {/* Sync Status */}
      {syncStatus && (
        <Card>
          <CardHeader title="Sync Status" />
          <CardBody className="space-y-4">
            <div className="flex items-center gap-3">
              <StatusIcon size={24} className={`${config?.color} ${config?.spin ? 'animate-spin' : ''}`} />
              <div>
                <Badge variant={config?.badge}>{config?.label}</Badge>
                <p className="text-xs text-slate-500 mt-1">Job ID: {syncStatus.ingestionJobId}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{syncStatus.documentsScanned}</p>
                <p className="text-xs text-slate-500">Scanned</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{syncStatus.documentsIndexed}</p>
                <p className="text-xs text-slate-500">Indexed</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-700">{syncStatus.documentsFailed}</p>
                <p className="text-xs text-slate-500">Failed</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-700">{syncStatus.documentsDeleted}</p>
                <p className="text-xs text-slate-500">Deleted</p>
              </div>
            </div>

            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-500">Started</span>
                <span className="text-slate-900">{new Date(syncStatus.startedAt).toLocaleString()}</span>
              </div>
              {syncStatus.completedAt && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Completed</span>
                  <span className="text-slate-900">{new Date(syncStatus.completedAt).toLocaleString()}</span>
                </div>
              )}
            </div>

            {syncStatus.failureReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-medium text-red-700 mb-1">Failure Reason</p>
                <p className="text-sm text-red-600">{syncStatus.failureReason}</p>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Info */}
      <Card>
        <CardBody>
          <h3 className="text-sm font-semibold text-slate-900 mb-2">How Sync Works</h3>
          <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
            <li>Upload documents via the Documents page</li>
            <li>Each document gets a metadata sidecar file in S3 for building-level filtering</li>
            <li>Trigger a sync to start the Bedrock ingestion job</li>
            <li>Bedrock processes documents, creates embeddings, and indexes them</li>
            <li>Once indexed, documents are searchable via the Chat feature</li>
          </ol>
        </CardBody>
      </Card>
    </div>
  )
}
